import React, {
	memo,
	isValidElement,
	useEffect,
	useState,
	useRef,
} from 'react';
import classNames from 'classnames';

// import Portalify from '../Portalify';
import { If, Then, Portalify } from '@buyhatke/react-ui';

import { calculatePositions } from './utils';

function isNode(o) {
	return typeof Node === 'object'
		? o instanceof Node
		: o &&
				typeof o === 'object' &&
				typeof o.nodeType === 'number' &&
				typeof o.nodeName === 'string';
}

const BbTooltips = ({
	style,
	wrappedContent,
	mini,
	children,
	ttStyle,
	onClick,
	className = '',
	clickFocus,
	inFocus,
	hoverFocus,
	position = 'bottom',
	dark,
	primary,
	arrow = true,
	gap = 5,
	padding = 8,
}) => {
	const referenceElement = useRef();
	const popperElement = useRef();
	const arrowElement = useRef();

	const [openState, setOpenState] = useState(false);
	const [
		{ tooltipStyling, arrowStyling, arrowWrapperStyling },
		setDynamicStyling,
	] = useState({
		tooltipStyling: {},
		arrowStyling: {},
		arrowWrapperStyling: {},
	});

	useEffect(
		() => {
			const onClickHandlerForDoc = docEv => {
				if (clickFocus && openState) {
					if (
						!!popperElement.current &&
						isNode(docEv.target) &&
						!popperElement.current.contains(docEv.target)
					) {
						setOpenState(false);
					}
				}
			};
			const updatePosition = () => {
				window.requestAnimationFrame(() => {
					const newPosition = calculatePositions(
						referenceElement.current,
						popperElement.current,
						arrowElement.current,
						position,
						gap,
						padding
					);
					if (newPosition !== null) {
						setDynamicStyling(newPosition);
					}
				});
			};

			if (openState) {
				updatePosition();
				window.addEventListener('resize', updatePosition);
				window.addEventListener('scroll', updatePosition);

				if (clickFocus) {
					document.addEventListener('click', onClickHandlerForDoc);
				}
			}
			return () => {
				if (openState) {
					window.removeEventListener('resize', updatePosition);
					window.removeEventListener('scroll', updatePosition);

					if (clickFocus) {
						document.removeEventListener(
							'click',
							onClickHandlerForDoc
						);
					}
				}
			};
		},
		// eslint-disable-next-line
		[openState]
	);

	useEffect(() => {
		if (typeof inFocus === 'boolean') {
			setOpenState(inFocus);
		}
	}, [inFocus]);

	return (
		<div
			ref={referenceElement}
			tabIndex={1}
			onMouseEnter={() => {
				if (hoverFocus) {
					setOpenState(true);
				}
			}}
			onMouseLeave={e => {
				if (
					hoverFocus &&
					!!popperElement.current &&
					isNode(e.relatedTarget) &&
					!popperElement.current.contains(e.relatedTarget)
				) {
					setOpenState(false);
				}
			}}
			onClick={e => {
				if (clickFocus) {
					if (
						openState &&
						!popperElement.current.contains(e.target) &&
						isNode(e.target) &&
						referenceElement.current.contains(e.target)
					) {
						setOpenState(false);
					} else {
						setOpenState(true);
					}
				}
				if (typeof onClick === 'function') {
					onClick();
				}
			}}
			className={`${className} relative`}
			style={{ style }}
		>
			<If condition={openState}>
				<Then>
					<div
						className="absolute top-0 left-0"
						style={arrowWrapperStyling}
					/>
				</Then>
			</If>

			{isValidElement(wrappedContent) ||
			/^(string|number)$/.test(typeof wrappedContent)
				? wrappedContent
				: wrappedContent()}

			<Portalify id="tooltip-container">
				<If condition={openState}>
					<Then>
						<If condition={arrow}>
							<Then>
								<div
									ref={arrowElement}
									className={classNames(
										'absolute w-5 h-5 border z-max transform rotate-45 pointer-events-none',
										{
											'bg-black border-black': dark,
										},
										{ 'border-gray-400 bg-white': !dark },
										{ 'bg-primary': primary }
									)}
									style={arrowStyling}
								/>
							</Then>
						</If>
						<div
							ref={popperElement}
							className={classNames(
								'absolute z-max text-shadow-none border shadow-md rounded-sm',
								{
									'bg-black text-white border-black': dark,
								},
								{
									'border-gray-400 bg-white': !dark,
								},
								{ 'bg-primary text-white': primary }
							)}
							style={{
								maxWidth: mini ? '200px' : 'none',
								...ttStyle,
								...tooltipStyling,
							}}
							onMouseLeave={e => {
								if (
									hoverFocus &&
									isNode(e.relatedTarget) &&
									!referenceElement.current.contains(
										e.relatedTarget
									)
								) {
									setOpenState(false);
								}
							}}
						>
							{children}
						</div>
					</Then>
				</If>
			</Portalify>
		</div>
	);
};

export default memo(BbTooltips);
